import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import { Button, CircularProgress, Grid } from "@material-ui/core";
import {
  format,
  subDays,
  addDays,
  subMonths,
  subYears,
  addMonths,
  addYears,
} from "date-fns";
import { useAuth } from "../../../firebase/useAuth";
import usePrepFqAnalytics from "../fetchers/usePrepFqAnalytics";
import {
  IncomingPrepFqAnalyticsQuery,
  PrepFqAnalyticsItem,
  QueryInterval,
} from "../../../models/prepFqQuiz";
import useUser from "../../../firebase/useUser";
import usePrepFqProfile from "../fetchers/usePrepFqProfile";
import useAveragePrepFqScores from "../fetchers/useAveragePrepFqScores";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import { makeStyles } from "@material-ui/core/styles";
import "date-fns";
import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import clsx from "clsx";
import useMediaQuery from "@material-ui/core/useMediaQuery";
const ResponsiveLine = dynamic(
  () => import("@nivo/line").then((mod) => mod.ResponsiveLine),
  { ssr: false }
);
const CustomSymbol = ({ size, color, borderWidth, borderColor, hide }: any) => {
  if (hide) {
    return (
      <g>
        <circle
          fill="#fff"
          r={size / 2}
          strokeWidth={borderWidth}
          stroke={borderColor}
        />
      </g>
    );
  } else {
    return (
      <g>
        <circle
          fill="#fff"
          r={size / 2}
          strokeWidth={borderWidth}
          stroke={borderColor}
        />
        <circle
          r={size / 5}
          strokeWidth={borderWidth}
          stroke={borderColor}
          fill={color}
          fillOpacity={0.35}
        />
      </g>
    );
  }
};

const getScoreSlice = (slice, avgScores, t) => {
  return (
    <div
      style={{
        background: "white",
        padding: "9px 12px",
        border: "1px solid #ccc",
      }}
    >
      {slice.points.map((point) => {
        if (point.serieId === t("prepFq:report.scoreGraph.score")) {
          return (
            <div
              key={point.id}
              style={{
                color:
                  point.data.y >= avgScores?.average_score
                    ? point.serieColor
                    : "#5db2e3",
                padding: "3px 0",
                fontSize: 16,
              }}
            >
              <strong
                style={{
                  color:
                    point.data.y >= avgScores?.average_score
                      ? point.serieColor
                      : "#5db2e3",
                }}
              >
                {t("prepFq:report.scoreGraph.score")}
              </strong>
              : {Math.round(point.data.y as number)} / 100
              <div style={{ marginTop: 8 }}>
                {format(point.data.x as any, "MMM dd, yyyy")}
              </div>
            </div>
          );
        } else if (point.serieId === t("prepFq:report.scoreGraph.best")) {
          return (
            <div
              key={point.id}
              style={{
                color:
                  point.data.y >= avgScores?.average_score
                    ? point.serieColor
                    : "#5db2e3",
                padding: "3px 0",
                fontSize: 16,
              }}
            >
              <strong
                style={{
                  color:
                    point.data.y >= avgScores?.average_score
                      ? point.serieColor
                      : "#5db2e3",
                }}
              >
                {t("prepFq:report.scoreGraph.best")}
              </strong>
              : {Math.round(point.data.y as number)} / 100
            </div>
          );
        } else if (point.serieId === t("prepFq:report.scoreGraph.average")) {
          return (
            <div
              key={point.id}
              style={{
                padding: "3px 0",
                fontSize: 16,
              }}
            >
              <strong
                style={{
                  color: point.serieColor,
                }}
              >
                {t("prepFq:report.scoreGraph.average")}
              </strong>
              : {Math.round(point.data.y as number)} / 100
            </div>
          );
        }
      })}
    </div>
  );
};

const DashedSolidLine = ({ series, lineGenerator, xScale, yScale }) => {
  return series.map(({ id, data, color, marker }) => {
    return (
      <path
        key={id}
        d={lineGenerator(
          data.map((d) => ({
            x: xScale(d.data.x),
            y: yScale(d.data.y),
          }))
        )}
        fill="none"
        stroke={color}
        style={
          marker !== "score"
            ? {
                // simulate line will dash stroke when index is even
                strokeDasharray: "4, 3",
                strokeWidth: 2,
              }
            : {
                // simulate line with solid stroke
                strokeWidth: 1,
              }
        }
      />
    );
  });
};

const NivoBox: React.FC<{
  height?: string;
  loading: boolean;
  children?: React.ReactNode;
}> = ({ children, height, loading }) => (
  <div style={{ position: "relative" }}>
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div
        style={{
          height: height || "800px",
          position: "absolute",
          width: "100%",
          bottom: 0,
          left: 0,
          right: 0,
          top: 0,
          display: loading ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255,255,255, 0.8)",
          zIndex: 100,
          flexDirection: "row",
          opacity: loading ? 1 : 0,
        }}
      >
        <CircularProgress size={48} />
      </div>
      <div style={{ height: height || "800px" }}>{children}</div>
    </div>
  </div>
);

const useStyles = makeStyles((theme) => ({
  buttonsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  buttonGroup: {
    borderRadius: 0,
    boxShadow: "none",
  },
  buttonsGrid: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    "@media (max-width: 960px)": {
      justifyContent: "center",
      marginTop: 12,
    },
  },
  title: {
    fontSize: 24,
    textAlign: "center",
    marginBottom: 24,
  },
  dateGrid: {
    marginLeft: 12,
    "@media (max-width: 960px)": {
      marginTop: 12,
      marginLeft: 0,
    },
  },
  centered: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const maxDataPoints = 30;
const axisBuffer = 5;
const PrepFqScoreGraph = () => {
  const { t } = useTranslation(["prepFq"]);
  const classes = useStyles();
  const authUser = useAuth();
  const matched = useMediaQuery("(max-width:960px)");
  const { user } = useUser(authUser?.user?.uid);
  const { profile, isLoading: profileLoading } = usePrepFqProfile(
    user?.user_id
  );
  const { avgScores, isLoading: averagesLoading } = useAveragePrepFqScores(
    profile?.prep_fq_profile_id,
    user,
    profile
  );
  const [query, setQuery] = useState<IncomingPrepFqAnalyticsQuery>({
    start_date: subMonths(new Date().getTime(), 6).getTime(),
    end_date: new Date().getTime(),
    interval: "monthly",
  });
  const {
    analyticsItems,
    isLoading: analyticsLoading,
    isValidating: analyticsReloading,
  } = usePrepFqAnalytics(
    authUser?.user?.uid,
    query.start_date,
    query.end_date,
    query.interval
  );
  const [show, setShow] = useState(false);
  const [data, setData] = useState<
    {
      id: string;
      marker: "score" | "best" | "average";
      data: { x: string; y: number }[];
    }[]
  >([
    {
      id: t("prepFq:report.scoreGraph.score"),
      marker: "score",
      data: [],
    },
    {
      id: t("prepFq:report.scoreGraph.average"),
      marker: "average",
      data: [],
    },
    {
      id: t("prepFq:report.scoreGraph.best"),
      marker: "best",
      data: [],
    },
  ]);
  const loading = averagesLoading || analyticsLoading || analyticsReloading;
  useEffect(() => {
    let timeout;
    if (!show && !loading) {
      timeout = setTimeout(() => {
        setShow(true);
      }, 300);
    }
    return function cleanup() {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [show, loading]);
  useEffect(() => {
    if (avgScores.average_score && analyticsItems && !loading) {
      let items = [];
      let bests = [];
      let averages = [];
      analyticsItems.forEach((item) => {
        const dataPoint = {
          x: format(new Date(item.date_created), "yyyy-MM-dd"),
          y: item.score,
        };
        const averagePoint = {
          x: dataPoint.x,
          y: avgScores.average_score,
        };
        const bestPoint = {
          x: dataPoint.x,
          y: avgScores.best_score,
        };
        items.push(dataPoint);
        averages.push(averagePoint);
        bests.push(bestPoint);
      });
      if (items.length > maxDataPoints) {
        const filterBy = Math.round(items.length / maxDataPoints);
        items = items.slice().filter((_, index) => index % filterBy === 0);
      }
      if (averages.length === 1 || bests.length === 1) {
        const average = averages[0];
        averages = [
          {
            y: average.y,
            x:
              query.interval === "monthly"
                ? format(subMonths(new Date(average.x), 1), "yyyy-MM-dd")
                : format(subYears(new Date(average.x), 1), "yyyy-MM-dd"),
          },
          average,
          {
            y: average.y,
            x:
              query.interval === "monthly"
                ? format(addMonths(new Date(average.x), 1), "yyyy-MM-dd")
                : format(addYears(new Date(average.x), 1), "yyyy-MM-dd"),
          },
        ];
        const best = bests[0];
        bests = [
          {
            y: best.y,
            x:
              query.interval === "monthly"
                ? format(subMonths(new Date(best.x), 1), "yyyy-MM-dd")
                : format(subYears(new Date(best.x), 1), "yyyy-MM-dd"),
          },
          best,
          {
            y: best.y,
            x:
              query.interval === "monthly"
                ? format(addMonths(new Date(best.x), 1), "yyyy-MM-dd")
                : format(addYears(new Date(best.x), 1), "yyyy-MM-dd"),
          },
        ];
      }
      setData([
        {
          id: t("prepFq:report.scoreGraph.score"),
          marker: "score",
          data: items,
        },
        {
          id: t("prepFq:report.scoreGraph.average"),
          marker: "average",
          data: averages,
        },
        {
          id: t("prepFq:report.scoreGraph.best"),
          marker: "best",
          data: bests,
        },
      ]);
    }
  }, [
    show,
    loading,
    avgScores?.average_score,
    query.interval,
    analyticsItems,
    t,
    avgScores?.best_score,
  ]);
  const getXRange = (interval: QueryInterval) => {
    if (analyticsItems) {
      const intervalText = interval === "monthly" ? "months" : "years";
      if (matched) {
        if (interval === "monthly") {
          return `every 6 ${intervalText}`;
        } else if (interval === "yearly") {
          return `every 2 ${intervalText}`;
        }
      } else if (analyticsItems.length <= 7) {
        return `every 1 ${intervalText}`;
      } else if (analyticsItems.length <= 13) {
        return `every 2 ${intervalText}`;
      } else if (analyticsItems.length <= 19) {
        return `every 3 ${intervalText}`;
      } else if (analyticsItems.length <= 25) {
        return `every 4 ${intervalText}`;
      } else if (analyticsItems.length <= 37) {
        return `every 6 ${intervalText}`;
      } else if (analyticsItems.length <= 73) {
        return `every 12 ${intervalText}`;
      } else {
        if (interval === "monthly") {
          return `every 6 ${intervalText}`;
        } else if (interval === "yearly") {
          return `every 2 ${intervalText}`;
        }
      }
    } else {
      return "every 1 months";
    }
  };
  const getXPrecision = (interval: QueryInterval) => {
    if (interval === "daily") {
      return "day";
    } else if (interval === "weekly") {
      return "day";
    } else if (interval === "monthly") {
      return "month";
    } else if (interval === "yearly") {
      return "year";
    } else {
      return "month";
    }
  };
  const getYBoundariesMax = (analyticsItems: PrepFqAnalyticsItem[]) => {
    // if (analyticsItems) {
    //   const max = Math.max.apply(
    //     Math,
    //     analyticsItems.map((analyticsItems) => analyticsItems.score)
    //   );
    //   return max + axisBuffer < 100 ? max + axisBuffer : 100;
    // } else {
    //   return 100;
    // }
    return 100;
  };
  const getYBoundariesMin = (analyticsItems: PrepFqAnalyticsItem[]) => {
    // if (analyticsItems) {
    //   const min = Math.min.apply(
    //     Math,
    //     analyticsItems.map((analyticsItems) => analyticsItems.score)
    //   );
    //   return min - axisBuffer > 0 ? min - axisBuffer : 0;
    // } else {
    //   return 0;
    // }
    return 0;
  };
  const getColor = (point) => {
    if (point.marker === "average") {
      return "#002655";
    } else if (point.marker === "score") {
      return "#002655FF";
    } else if (point.marker === "best") {
      return "#5DB2E3";
    }
  };
  const getPointSymbol = (point) => {
    if (point.color === "#002655") {
      return (
        <CustomSymbol
          size={1}
          color={point.color}
          borderWidth={1}
          borderColor={point.color}
          hide
        />
      );
    } else if (point.color === "#002655FF") {
      return (
        <CustomSymbol
          size={16}
          color={point.color}
          borderWidth={1}
          borderColor={point.color}
        />
      );
    } else if (point.color === "#5DB2E3") {
      return (
        <CustomSymbol
          size={1}
          color={point.color}
          borderWidth={1}
          borderColor={point.color}
          hide
        />
      );
    }
  };
  const updateInterval = (interval: QueryInterval) => (e) => {
    if (interval === "yearly") {
      setQuery({
        ...query,
        interval,
        end_date: query.end_date,
        start_date: subYears(new Date().getTime(), 6).getTime(),
      });
    } else if (interval === "monthly") {
      setQuery({
        ...query,
        interval,
        end_date: query.end_date,
        start_date: subMonths(new Date().getTime(), 6).getTime(),
      });
    }
  };
  const updateStartDate = (e: Date) => {
    setQuery({
      ...query,
      start_date: e.getTime(),
    });
  };
  const updateEndDate = (e: Date) => {
    setQuery({
      ...query,
      end_date: e.getTime(),
    });
  };
  return (
    <div style={{ marginBottom: 64 }}>
      <Grid>
        <Grid xs={12} item>
          <p className={classes.title}>{t("prepFq:report.scoreGraph.title")}</p>
        </Grid>
      </Grid>
      <NivoBox height={"400px"} loading={loading}>
        <ResponsiveLine
          curve={"natural"}
          data={data}
          animate={true}
          margin={{ top: 50, right: 50, bottom: 50, left: 100 }}
          xScale={{
            type: "time",
            format: "%Y-%m-%d",
            useUTC: true,
            precision: getXPrecision(query.interval),
          }}
          xFormat="time:%Y-%m-%d"
          yScale={{
            type: "linear",
            stacked: false,
            max: getYBoundariesMax(analyticsItems),
            min: getYBoundariesMin(analyticsItems),
          }}
          yFormat=" >-.0f"
          colors={getColor}
          axisTop={null}
          axisRight={null}
          axisLeft={{
            legend: "Preparedness Score",
            legendOffset: -50,
            legendPosition: "middle",
            format: " >-.0f",
            tickValues: 5,
          }}
          axisBottom={{
            format: "%b %d, 20%y",
            tickValues: getXRange(query.interval),
            legendOffset: -12,
            legendPosition: "middle",
          }}
          enablePointLabel={false}
          pointSymbol={getPointSymbol}
          pointSize={24}
          pointBorderWidth={1}
          pointBorderColor={{
            from: "color",
            modifiers: [["brighter", 0.3]],
          }}
          useMesh={true}
          enableSlices="x"
          sliceTooltip={({ slice }) => {
            return getScoreSlice(slice, avgScores, t);
          }}
          theme={{
            markers: {
              fontSize: "14px",
            },
            dots: {
              text: {
                fontSize: "14px",
              },
            },
            labels: {
              text: {
                fontSize: "14px",
              },
            },
            axis: {
              ticks: {
                text: {
                  fontSize: "14px",
                },
              },
              domain: {
                line: {
                  fontSize: "14px",
                },
              },
              legend: {
                text: {
                  fontSize: "14px",
                },
              },
            },
          }}
          layers={[
            // includes all default layers
            "grid",
            "markers",
            "axes",
            "areas",
            "crosshair",
            "slices",
            "points",
            "mesh",
            "legends",
            DashedSolidLine, // add the custome layer here
          ]}
        />
      </NivoBox>
      <Grid container style={{ padding: "0 24px" }}>
        <Grid className={classes.buttonsGrid} md={9} sm={12} xs={12} item>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid
              className={classes.buttonsGrid}
              container
              style={{ justifyContent: "flex-start" }}
            >
              <Grid className={classes.centered} md="auto" sm={12} xs={12} item>
                <KeyboardDatePicker
                  variant="inline"
                  format="MMMM d, yyyy"
                  margin="normal"
                  id="start-date"
                  label={t("prepFq:report.scoreGraph.start")}
                  value={new Date(query.start_date)}
                  onChange={updateStartDate}
                  inputVariant="outlined"
                  maxDate={
                    query.end_date < subDays(new Date(), 1).getTime()
                      ? new Date(query.end_date)
                      : subDays(new Date(), 1)
                  }
                  maxDateMessage={t("prepFq:report.scoreGraph.maxMessage")}
                  autoOk
                />
              </Grid>
              <Grid
                md="auto"
                sm={12}
                xs={12}
                className={clsx(classes.dateGrid, classes.centered)}
                item
              >
                <KeyboardDatePicker
                  variant="inline"
                  format="MMMM d, yyyy"
                  margin="normal"
                  id="end-date"
                  label={t("prepFq:report.scoreGraph.end")}
                  value={new Date(query.end_date)}
                  onChange={updateEndDate}
                  inputVariant="outlined"
                  disableFuture
                  minDate={addDays(new Date(query.start_date), 1)}
                  minDateMessage={t("prepFq:report.scoreGraph.minMessage")}
                  autoOk
                />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid className={classes.buttonsGrid} md={3} sm={12} xs={12} item>
          <div className={classes.buttonsContainer}>
            <ButtonGroup
              variant="contained"
              aria-label="contained primary button group"
              className={classes.buttonGroup}
            >
              <Button
                onClick={updateInterval("monthly")}
                style={
                  query.interval === "monthly"
                    ? {
                        backgroundColor: "#002655",
                        color: "white",
                      }
                    : undefined
                }
              >
                {t("prepFq:report.scoreGraph.intervals.monthly")}
              </Button>
              <Button
                onClick={updateInterval("yearly")}
                style={
                  query.interval === "yearly"
                    ? {
                        backgroundColor: "#002655",
                        color: "white",
                      }
                    : undefined
                }
              >
                {t("prepFq:report.scoreGraph.intervals.yearly")}
              </Button>
            </ButtonGroup>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default PrepFqScoreGraph;

{
  /* <Button
  onClick={updateInterval("daily")}
  style={
    query.interval === "daily"
      ? {
          backgroundColor: "#002655",
          color: "white",
        }
      : undefined
  }
>
  {t("prepFq:report.scoreGraph.intervals.daily")}
</Button>
<Button
  onClick={updateInterval("weekly")}
  style={
    query.interval === "weekly"
      ? {
          backgroundColor: "#002655",
          color: "white",
        }
      : undefined
  }
>
  {t("prepFq:report.scoreGraph.intervals.weekly")}
</Button>  */
}
